import { el, clear } from "./dom.js";
import { magButton } from "./magazine/kit.js";
import { MAG } from "../utils/magazine.js";
import { debounce } from "../utils/debounce.js";
import { searchComicSeries, comicsErrorMessage } from "../api/comics.js";
import { parseRanges, formatRanges } from "../utils/comics.js";

// "Collects…" editor sheet for a collected edition: pick the series (searched on
// Metron, or typed by hand when Metron doesn't have it) and the issue numbers it
// contains ("188–199, 201"). Resolves { series_id, series_label, ranges } or null.
// `initial` pre-fills it for editing an existing entry.

export function showCollectsSheet({ initial = null, suggestion = "" } = {}) {
	return new Promise((resolve) => {
		let settled = false;
		const finish = (value) => {
			if (settled) return;
			settled = true;
			search.cancel();
			overlay.remove();
			document.removeEventListener("keydown", onKey);
			resolve(value);
		};
		const onKey = (e) => {
			if (e.key === "Escape") finish(null);
		};
		document.addEventListener("keydown", onKey);

		let picked = initial
			? { series_id: initial.series_id ?? null, series_label: initial.series_label }
			: null;

		const errorLine = el("div.modal__hint", { style: { color: "#ff5a4a", minHeight: "1em" } });
		const pickedLine = el("div", {
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "12px",
				color: MAG.ink, padding: "6px 0 2px",
			},
		});
		const results = el("div", {
			style: {
				display: "flex", flexDirection: "column", gap: "4px", maxHeight: "220px",
				overflowY: "auto", margin: "6px 0 0",
			},
		});
		const queryInput = el("input.field__select", {
			type: "search",
			placeholder: "Search a series on Metron…",
			value: initial ? "" : suggestion,
			oninput: (e) => search(e.currentTarget.value.trim()),
		});
		const rangesInput = el("input.field__select", {
			type: "text",
			inputmode: "numeric",
			placeholder: "e.g. 188–199, 201",
			value: initial?.ranges ?? "",
			oninput: () => {
				errorLine.textContent = "";
			},
		});

		function paintPicked() {
			pickedLine.textContent = picked
				? `✓ ${picked.series_label}${picked.series_id == null ? " (typed by hand)" : ""}`
				: "No series picked yet.";
			pickedLine.style.color = picked ? MAG.lime : MAG.dim;
		}

		function row(label, sub, onPick) {
			return el(
				"button",
				{
					type: "button",
					onclick: onPick,
					style: {
						textAlign: "left", cursor: "pointer", borderRadius: "10px", padding: "8px 10px",
						background: "rgba(255,255,255,.06)", border: `1px solid ${MAG.line}`, color: MAG.ink,
					},
				},
				el("div", { style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "12.5px" } }, label),
				sub
					? el("div", { style: { fontFamily: "'Archivo', sans-serif", fontSize: "10.5px", color: MAG.dim, marginTop: "2px" } }, sub)
					: null,
			);
		}

		let inflight = null;
		async function runSearch(q) {
			if (inflight) inflight.abort();
			clear(results);
			if (!q) return;
			const controller = new AbortController();
			inflight = controller;
			results.append(el("div.modal__hint", {}, "Searching…"));
			try {
				const page = await searchComicSeries(q, { signal: controller.signal });
				if (controller.signal.aborted) return;
				clear(results);
				for (const s of page.results) {
					const sub = [s.publisher, s.type, s.issue_count ? `${s.issue_count} issues` : null]
						.filter(Boolean)
						.join(" · ");
					results.append(
						row(s.label, sub, () => {
							picked = { series_id: s.series_id, series_label: s.label };
							paintPicked();
							rangesInput.focus();
						}),
					);
				}
				// Escape hatch: a series Metron doesn't know.
				results.append(
					row(`Use “${q}” as typed`, "Not on Metron — keep the name as you wrote it", () => {
						picked = { series_id: null, series_label: q };
						paintPicked();
						rangesInput.focus();
					}),
				);
			} catch (err) {
				if (err.name === "AbortError") return;
				clear(results);
				results.append(el("div.modal__hint", { style: { color: "#ff5a4a" } }, comicsErrorMessage(err)));
				results.append(
					row(`Use “${q}” as typed`, "Keep the name as you wrote it", () => {
						picked = { series_id: null, series_label: q };
						paintPicked();
					}),
				);
			} finally {
				if (inflight === controller) inflight = null;
			}
		}
		// Metron searches cost budget (20/min): wait for the typing to settle.
		const search = debounce(runSearch, 700);

		function save() {
			if (!picked) {
				errorLine.textContent = "Pick a series first.";
				return;
			}
			const parsed = parseRanges(rangesInput.value);
			if (parsed.error) {
				errorLine.textContent = parsed.error;
				return;
			}
			finish({ ...picked, ranges: formatRanges(parsed.ranges) });
		}

		paintPicked();
		const overlay = el(
			"div.modal-overlay",
			{
				onclick: (e) => {
					if (e.target === overlay) finish(null);
				},
			},
			el(
				"div.modal",
				{},
				el("h2.modal__title", {}, initial ? "Edit collected issues" : "Add collected issues"),
				el("div.field", {}, el("label.field__label", {}, "Series"), queryInput, results, pickedLine),
				el("div.field", {}, el("label.field__label", {}, "Issues"), rangesInput),
				errorLine,
				el(
					"div",
					{ style: { display: "flex", gap: "8px", marginTop: "10px" } },
					magButton("Save", { block: true, onClick: save }),
					magButton("Cancel", { variant: "ghost", block: true, onClick: () => finish(null) }),
				),
			),
		);
		document.body.appendChild(overlay);
		if (!initial && suggestion) search(suggestion);
		queryInput.focus();
	});
}
